import { gql } from '@apollo/client';
import IconAdd from '@apollo/icons/default/IconAdd.svg';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconStar from '@apollo/icons/default/IconStar.svg';
import { Button, Group, IconButton } from '@apollo/orbit';
import classnames from 'classnames';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { TruncateWithTooltip } from 'src/components/TruncateWithTooltip';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  collectionSpecificShortcodeText,
  operationSpecificShortcodeText,
} from './shortcodes/shortcodesConfig';

export const CollectionEntryOperationFragment = gql`
  fragment CollectionEntryOperationFragment on OperationCollectionEntry {
    id
    name
  }
`;

export const CollectionEntryFragment = gql`
  fragment CollectionEntryFragment on OperationCollection {
    id
    isFavorite
    name
    description
    isShared
    operations {
      id
      ...CollectionEntryOperationFragment
    }
  }
  ${CollectionEntryOperationFragment}
`;

export const OperationCollectionsSection = ({
  editor,
  variantSharedCollectionsWithOperations,
  loading,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor | undefined | null;
  variantSharedCollectionsWithOperations: GraphQLTypes.OperationCollectionReadMeQuery_service_variant_operationCollections[];
  loading: boolean;
}) => {
  const handleInsertOperationShortcode = useCallback(
    (operation: GraphQLTypes.CollectionEntryOperationFragment) => {
      if (!editor) return;
      const selection = editor.getSelection();
      if (!selection) return;
      const collectionNameForOperation =
        variantSharedCollectionsWithOperations.find((collection) =>
          collection.operations.find((op) => op.id === operation.id),
        )?.name;
      const commentText = collectionNameForOperation
        ? `${collectionNameForOperation} / ${operation.name}`
        : 'Operation not found';
      const edit: monacoEditor.editor.IIdentifiedSingleEditOperation = {
        range: selection,
        text: `[//]: # "Saved Operation: ${commentText}"

{{ ${operationSpecificShortcodeText(operation.id)} }}`,
        forceMoveMarkers: false,
      };
      editor.executeEdits('insert operation id', [edit]);
    },
    [editor, variantSharedCollectionsWithOperations],
  );

  const handleInsertCollectionLink = useCallback(
    (collectionId: string) => {
      if (!editor) return;
      const selection = editor.getSelection();
      if (!selection) return;
      const edit: monacoEditor.editor.IIdentifiedSingleEditOperation = {
        range: selection,
        text: `[//]: # "Operation Collection: ${
          variantSharedCollectionsWithOperations.find(
            (collection) => collection.id === collectionId,
          )?.name ?? 'Collection not found'
        }"

{{ ${collectionSpecificShortcodeText(collectionId)} }}`,
        forceMoveMarkers: false,
      };
      editor.executeEdits('insert operation id', [edit]);
    },
    [editor, variantSharedCollectionsWithOperations],
  );
  if (loading) {
    return (
      <div className="h-12">
        <Loading size="sm" />
      </div>
    );
  }

  return (
    <div className="text-lg font-normal">
      <div className="mb-2 text-sm text-secondary">
        Shared across your organization
      </div>
      {variantSharedCollectionsWithOperations.map((collection) => (
        <CollectionEntry
          key={collection.id}
          collection={collection}
          handleInsertOperationShortcode={handleInsertOperationShortcode}
          handleInsertCollectionLink={handleInsertCollectionLink}
        />
      ))}
    </div>
  );
};

const CollectionEntry = ({
  collection,
  handleInsertOperationShortcode,
  handleInsertCollectionLink,
}: {
  collection: GraphQLTypes.CollectionEntryFragment;
  handleInsertOperationShortcode: (
    operation: GraphQLTypes.CollectionEntryOperationFragment,
  ) => void;
  handleInsertCollectionLink: (collection: string) => void;
}) => {
  const trackCustomEvent = useTrackCustomEvent();
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div key={collection.id}>
      <div
        className={classnames(
          'flex items-center justify-between text-sm',
          !expanded && 'mb-1',
        )}
      >
        <Group className="-ml-3 overflow-hidden pl-3">
          <Tooltip label={`Insert link to ${collection.name}`}>
            <IconButton
              role="button"
              aria-label={`Insert link to ${collection.name}`}
              variant="hidden"
              className="-ml-2"
              size="sm"
              onClick={() => {
                handleInsertCollectionLink(collection.id);
                trackCustomEvent({
                  category: 'README Shortcodes',
                  action: `Add operation collection`,
                });
              }}
              icon={<IconAdd className="text-icon-primary" />}
            />
          </Tooltip>
          <Button
            variant="hidden"
            size="sm"
            className="pl-1 pr-2"
            leftIcon={
              expanded ? (
                <IconChevronDown className="text-icon-primary" />
              ) : (
                <IconChevronRight className="text-icon-primary" />
              )
            }
            onClick={() => setExpanded(!expanded)}
          >
            <TruncateWithTooltip content={collection.name} />
          </Button>
        </Group>

        <div className="flex items-center">
          {collection.isFavorite && (
            <IconStar className="fill-icon-change stroke-icon-change" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="ml-2">
          {collection.operations.map((operation) => {
            return (
              <div
                className="mt-1 flex items-center text-sm"
                key={operation.id}
              >
                <Tooltip label={`Insert ${operation.name}`}>
                  <IconButton
                    variant="hidden"
                    aria-label={`Insert ${operation.name}`}
                    className="mr-2"
                    size="sm"
                    onClick={() => {
                      handleInsertOperationShortcode(operation);
                      trackCustomEvent({
                        category: 'README Shortcodes',
                        action: `Add operation collection entry`,
                      });
                    }}
                  >
                    <IconAdd className="text-icon-primary" />
                  </IconButton>
                </Tooltip>

                <TruncateWithTooltip content={operation.name} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
